/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */

import { ROUTE_SIGN_IN } from '@/routes/paths.js'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { fetchAuthSession, fetchUserAttributes, signOut, fetchMFAPreference } from 'aws-amplify/auth'
import { getCurrentUserSessionAccessToken, getUserDetails, removeUserDetails, saveUserDetails } from '@/storage'

import { END_POINTS } from '@/api/end-points'
import { Hub } from 'aws-amplify/utils'
import PropTypes from 'prop-types'
import { User } from '../classes/User'
import fetch from '@/api/fetch-with-authorization'
import { syncHighnoteData } from '@/api/sync-highnote-data'
import { syncPlaidItems } from '@/api/payment/plaid'
import { saveUpdateModalDismissed } from '../storage/plaid-provider'
import { useSharedLoginSession } from '@/hooks/use-shared-login-session'
import { linkFullstoryCustomerSession } from './fullstory.context'
import { linkAmplitudeSessionWithUserId, sendAmplitudeEvent } from './amplitude.context'
import Cookies from 'js-cookie'

const AuthContext = createContext()

const AuthProvider = ({ children, client }) => {
  const userInstance = new User(getUserDetails())
  const [user, setUserState] = useState(userInstance)
  const [accessToken, setAccessToken] = useState(getCurrentUserSessionAccessToken())
  const [loading, setLoading] = useState(false)
  const [isCardApplicationSubmitted, setIsCardApplicationSubmitted] = useState(false)
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false)
  const [isMFAEnabled, setIsMFAEnabled] = useState(false)
  const [isPhoneNumberVerified, setIsPhoneNumberVerified] = useState(false)
  const [oAuthError, setOAuthError] = useState('')
  const { startSessionTick, clearSessionTick, isActiveSession } = useSharedLoginSession()
  const authenticated = !!accessToken && isActiveSession()

  useEffect(() => {
    const logoutIfSessionExpired = async () => {
      try {
        if (!authenticated && accessToken) {
          await logout()
        }
      } catch (error) {
        console.error('Error during logoutIfSessionExpired:', error)
      }
    }
    logoutIfSessionExpired()
  }, [authenticated, accessToken])

  /**
   * Sets the user state with a new user instance and saves the user details to localStorage.
   *
   * @param {Object} newUserData - The new user data to update.
   */
  const setUser = newUserData => {
    setUserState(new User(newUserData))
    saveUserDetails(newUserData)
  }

  async function logout() {
    removeUserDetails()
    clearSessionTick()
    await signOut()
  }

  const refreshUserSession = useCallback(
    async ({ force = false } = {}) => {
      const session = await fetchAuthSession({ forceRefresh: force })
      setAccessToken(session.tokens?.accessToken.toString())
      setIsTokenRefreshing(false)
    },
    [isTokenRefreshing]
  )

  const refreshUser = async () => {
    const { customer } = await client.fetchQuery({
      queryKey: ['me'],
      queryFn: () => fetch({ path: END_POINTS.ME }),
    })
    return customer
  }

  const updateConsumerCardData = useCallback(async () => {
    const customer = await refreshUser()
    const updatedUser = {
      ...user,
      customer: {
        ...user?.customer,
        consumerCard: customer.consumerCard,
      },
    }
    setUser(updatedUser)
  })

  //NOTE:setIsCardApplicationSubmitted need any implementation
  const handleRoutes = useCallback(
    customerData => {
      if (customerData) {
        setIsCardApplicationSubmitted(true)
        // const isBannerDismissed = customerData.preferences.statusBannerDismissed ?? false
        // console.log('handleNavigation: isBannerDismissed =', isBannerDismissed)
      }
    },

    [isCardApplicationSubmitted, user]
  )

  const updateMFAStatus = async () => {
    setLoading(true)
    try {
      const mfaOutput = await fetchMFAPreference()
      setIsMFAEnabled(mfaOutput.enabled ? true : false)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const updatePhoneNumberStatus = async () => {
    try {
      const user = await fetchUserAttributes()
      setIsPhoneNumberVerified(user.phone_number_verified)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    updateMFAStatus()
  }, [isMFAEnabled])

  useEffect(() => {
    const fetchCustomerData = async () => {
      // fetchQuery because in some cases undefined is cached and reused, Better to trigger http request on signedIn
      const { customer } = await client.fetchQuery({
        queryKey: ['me'],
        queryFn: () => fetch({ path: END_POINTS.ME }),
      })
      return customer
    }

    const setUserContext = async ({ syncHighnote = false, syncPlaid = false } = {}) => {
      setLoading(true)
      try {
        const user = await fetchUserAttributes()
        const { tokens } = await fetchAuthSession()
        const customer = await fetchCustomerData()

        // Set FullStory identity with customer information
        linkFullstoryCustomerSession(customer)

        // Set Amplitude identity
        linkAmplitudeSessionWithUserId({ userId: user.sub })
        sendAmplitudeEvent(user.sub, 'Signed In', null, {
          last_sign_in: new Date().toISOString(),
          customer_id: customer?.customer_id,
        })

        setUser({ ...user, customer })
        handleRoutes(customer)
        setAccessToken(tokens.accessToken.toString())
        setIsPhoneNumberVerified(user.phone_number_verified)
        if (syncHighnote) {
          trySyncHighnoteData(customer)
        }
        if (syncPlaid) {
          trySyncPlaidData()
        }
        startSessionTick()
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    }

    // Synchonize Highnote data with the backend once after user is signed in
    const trySyncHighnoteData = async customer => {
      if (customer?.financialAccountId) {
        try {
          await syncHighnoteData(customer.financialAccountId)
          console.log('Highnote data synced successfully.')
        } catch (error) {
          console.error('Failed to sync Highnote data:', error)
        }
      }
    }

    // Synchronize Plaid data with the backend once after user is signed in
    const trySyncPlaidData = async () => {
      try {
        await syncPlaidItems()
        console.log('Plaid data synced successfully.')
      } catch (error) {
        console.error('Failed to sync Plaid data:', error)
      }
    }

    // Synchronize referral data from cookie if card application has not been submitted
    const trySyncReferralData = async () => {
      try {
        const refCookie = Cookies.get('refTracking')
        const result = await fetch({
          path: END_POINTS.SYNC_REFERRAL_DATA,
          method: 'PUT',
          body: JSON.stringify(refCookie ? { referralCookie: refCookie } : {}),
        })

        // If sync returns null cookie, delete the cookie
        if (result && !result.cookie) {
          Cookies.remove('refTracking')
          console.log('Referral sync returned null cookie, cleared refTracking cookie.')
        } else if (result && result.cookie) {
          // Set the cookie from the backend response (works for both promo codes and referrals)
          const jsonString = JSON.stringify(result.cookie)
          const base64Str = btoa(jsonString)
          Cookies.set('refTracking', base64Str, { expires: 30, secure: true, path: '/' })
          console.log('Referral data synced successfully, cookie updated.', result)
        } else {
          console.log('Referral data synced successfully.', result)
        }
      } catch (error) {
        console.error('Failed to sync referral data:', error)
      }
    }

    // If authenticated better to fire API request to update user with latest information if user refresh the page
    if (authenticated) setUserContext()

    // listen to amplify auth events
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('user have been signedIn successfully.')
          saveUpdateModalDismissed(false) // Show plaid update modal again, if relevant
          await setUserContext({ syncHighnote: true, syncPlaid: true })
          // Sync referral cookie/DB data after sign in (will short circuit if card app submitted)
          trySyncReferralData()
          break

        case 'signedOut':
          console.log('user have been signedOut successfully.')
          client.clear()
          window.location.href = ROUTE_SIGN_IN
          break

        case 'tokenRefresh':
          console.log('auth tokens have been refreshed.')
          setIsTokenRefreshing(false)
          break

        case 'tokenRefresh_failure':
          console.log('failure while refreshing auth tokens.')
          // NOTE: Not working due to many re-rendering
          // setIsTokenRefreshing(false)
          break

        case 'signInWithRedirect':
          console.log('signInWithRedirect API has successfully been resolved.')
          // To continue loading util user is redirected to the next page
          setLoading(true)
          await setUserContext()
          break

        case 'signInWithRedirect_failure':
          if (payload.data?.error?.message.includes('SSO login is not allowed for this email')) {
            setOAuthError('Account created manually. Please log in using your email and password instead.')
          } else {
            setOAuthError('An error has occurred during the OAuth flow.')
          }
          break

        case 'customOAuthState':
          console.log('custom state returned from CognitoHosted UI')
          break
      }
    })
    return () => unsubscribe()
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        logout,
        setUser,
        client,
        loading,
        setLoading,
        accessToken,
        isTokenRefreshing,
        setIsTokenRefreshing,
        authenticated,
        refreshUserSession,
        isCardApplicationSubmitted,
        setIsCardApplicationSubmitted,
        isMFAEnabled,
        updateMFAStatus,
        isPhoneNumberVerified,
        updatePhoneNumberStatus,
        updateConsumerCardData,
        oAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.element,
  client: PropTypes.shape({
    clear: PropTypes.func.isRequired,
    ensureQueryData: PropTypes.func.isRequired,
    fetchQuery: PropTypes.func.isRequired,
  }).isRequired,
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}

export { AuthProvider, useAuth }
